import apiClient from "@/src/services/api/axios";

type FeatureRequestIssueInput = Readonly<{
  title: string;
  description: string;
}>;

export type FeatureRequestIssue = Readonly<{
  issueNumber: string;
  projectUrl: string;
  /** Project card node id (`PVTI_…`); the Card Moved workflow matches on it. */
  projectItemId: string;
  issueNodeId: string;
  githubUpdatedAt: string;
}>;

type WorkflowWebhookResponse = Readonly<{
  status?: string;
  data?: {
    data?: {
      addProjectV2ItemById?: {
        item?: {
          id?: string;
          databaseId?: number;
          project?: { url?: string };
          content?: { id?: string; number?: number };
        };
      };
    };
  }[];
}>;

const WEBHOOK_TIMEOUT_MS = 30000;

// Card Moved only applies an event newer than the row's githubUpdatedAt. The
// data gateway ignores `eq: null`, so an unset value would never match — every
// linked row starts at the epoch instead.
const CARD_NEVER_SYNCED = "1970-01-01T00:00:00Z";

export const createFeatureRequestIssue = async ({
  title,
  description,
}: FeatureRequestIssueInput): Promise<FeatureRequestIssue | null> => {
  const path = process.env.EXPO_PUBLIC_FEATURE_REQUEST_WEBHOOK_PATH;
  if (!path) return null;

  try {
    const { data } = await apiClient.post<WorkflowWebhookResponse>(
      path,
      { title, description },
      { timeout: WEBHOOK_TIMEOUT_MS },
    );
    const item = data.data?.[0]?.data?.addProjectV2ItemById?.item;
    const issueNumber = item?.content?.number;
    const issueNodeId = item?.content?.id;
    const projectItemId = item?.id;
    const itemId = item?.databaseId;
    const boardUrl =
      process.env.EXPO_PUBLIC_FEATURE_REQUEST_PROJECT_URL ?? item?.project?.url;

    if (
      data.status !== "Completed" ||
      issueNumber == null ||
      itemId == null ||
      !projectItemId ||
      !issueNodeId ||
      !boardUrl
    ) {
      console.warn("Feature request webhook returned no issue", data);
      return null;
    }
    return {
      issueNumber: String(issueNumber),
      projectUrl: `${boardUrl}?pane=issue&itemId=${itemId}`,
      projectItemId,
      issueNodeId,
      githubUpdatedAt: CARD_NEVER_SYNCED,
    };
  } catch (error) {
    console.warn("Feature request webhook failed", error);
    return null;
  }
};

export const updateFeatureRequestIssue = async ({
  issueNumber,
  title,
  description,
}: FeatureRequestIssueInput & { issueNumber: string }): Promise<void> => {
  const path = process.env.EXPO_PUBLIC_FEATURE_REQUEST_UPDATE_WEBHOOK_PATH;
  if (!path) return;

  try {
    const { data } = await apiClient.post<WorkflowWebhookResponse>(
      path,
      { number: issueNumber, title, description },
      { timeout: WEBHOOK_TIMEOUT_MS },
    );
    if (data.status !== "Completed") {
      console.warn("Feature request update webhook did not complete", data);
    }
  } catch (error) {
    console.warn("Feature request update webhook failed", error);
  }
};
